import React, { FC, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Typography,
  CircularProgress,
  Alert,
  Box,
  Paper,
  Grid,
  IconButton,
  TableSortLabel,
  Button,
  Chip,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import useFileUpload from 'src/hooks/useFileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import InsightsIcon from '@mui/icons-material/Insights';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import LandscapeIcon from '@mui/icons-material/Landscape';
import PlaceIcon from '@mui/icons-material/Place';
import SettingsIcon from '@mui/icons-material/Settings';
import clientService from 'src/clientService';
import {
  DeploymentDetailResponse,
  DeviceType,
  FileAdminDetailWithUser,
  FileDownloadResponse,
  HttpResponse,
  HTTPValidationError,
  UserRole,
} from 'src/api/Api';
import { useAppState } from 'src/state-management/state-provider';
import { formatDistanceToNow } from 'date-fns';
import { TablePagination } from '@mui/material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import UploadStatusDialog from './UploadStatusDialog';
import AudioPlayer from 'src/view/components/AudioPlayer';
import ImageOverlay from 'src/view/components/ImageOverlay';
import ConfirmationDialog from 'src/view/components/ConfirmationDialog';
import InferenceViewerDialog from 'src/view/Admin/components/InferenceViewerDialog';
import { CalendarMonth, DataObject, CheckCircle, Error } from '@mui/icons-material';
import { formatDate } from 'src/util/date-utils';
import { useSnackbar } from 'src/view/components/SnackbarService';
import DeploymentDeviceCard from 'src/view/Admin/components/DeploymentDeviceCard';

interface UploadedFile {
  id: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed';
  error: string | null;
}

export enum FileStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  PROCESSING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

// Extend the auto-generated interface to include the missing status field
interface FileAdminDetailWithUserExtended extends FileAdminDetailWithUser {
  status: FileStatus;
}

type Order = 'asc' | 'desc';

interface Column {
  id:
    | keyof FileAdminDetailWithUserExtended
    | 'uploaded_by_user'
    | 'actions'
    | 'download'
    | 'inference'
    | 'spectrogram'
    | 'aci_indices'
    | 'inference_status';
  label: string;
  sortable?: boolean;
}

const columns: readonly Column[] = [
  { id: 'name', label: 'File Name', sortable: true },
  { id: 'uploaded_by_user', label: 'Uploaded By', sortable: false },
  { id: 'created_at', label: 'Uploaded At', sortable: true },
  { id: 'inference_status', label: 'Inference Status', sortable: false },
  { id: 'download', label: 'Download', sortable: false },
  { id: 'inference', label: 'Inference', sortable: false },
  { id: 'spectrogram', label: 'View Spectrogram', sortable: false },
  { id: 'aci_indices', label: 'Acoustic Indices', sortable: false },
  { id: 'actions', label: 'Actions', sortable: false },
];

const Upload: FC = () => {
  const { deploymentId } = useParams<{ deploymentId: string }>();
  const [masterData, setMasterData] = useState<DeploymentDetailResponse | null>(null);
  const [files, setFiles] = useState<FileAdminDetailWithUserExtended[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filesLoading, setFilesLoading] = useState<boolean>(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [uploadQueue, setUploadQueue] = useState<UploadedFile[]>([]);
  const [isUploadDialogVisible, setIsUploadDialogVisible] = useState(false);
  const [openInferenceViewerDialog, setOpenInferenceViewerDialog] = useState(false);
  const [currentInferenceData, setCurrentInferenceData] = useState<
    Record<string, any>[] | null | undefined
  >(undefined);
  const [selectedFile, setSelectedFile] = useState<FileAdminDetailWithUserExtended | null>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openMarkCompleteDialog, setOpenMarkCompleteDialog] = useState(false);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<
    keyof FileAdminDetailWithUserExtended | 'uploaded_by_user'
  >('created_at');
  const [inferencingFileIds, setInferencingFileIds] = useState<Set<string>>(new Set());
  const inferencingFileIdsRef = useRef<Set<string>>(new Set());
  const { showSnackbar, snackbarType } = useSnackbar();
  const { state } = useAppState();
  const isAdmin = state.user?.role?.includes(UserRole.Admin) ?? false;
  const [markingComplete, setMarkingComplete] = useState(false);

  // Keep ref in sync with state
  useEffect(() => {
    inferencingFileIdsRef.current = inferencingFileIds;
  }, [inferencingFileIds]);

  useEffect(() => {
    if (uploadQueue.length > 0) {
      setIsUploadDialogVisible(true);
    }
  }, [uploadQueue]);

  const handleClearFile = useCallback((id: string) => {
    setUploadQueue((prev) => prev.filter((file) => file.id !== id));
  }, []);

  const handleClearAllUploads = useCallback(() => {
    setUploadQueue([]);
    setIsUploadDialogVisible(false);
  }, []);

  const { uploadFile } = useFileUpload();

  // Define acceptedFormats unconditionally
  const acceptedFormats =
    masterData?.device?.type === DeviceType.AUDIO
      ? '.wav, .mp3, .flac, .ogg'
      : '.jpg, .jpeg, .png, .gif, .mp4, .avi, .mov';

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!deploymentId) {
        setFilesError('Deployment ID is missing.');
        return;
      }

      const existingFileNames = new Set(files.map((f) => f.name));

      const newFilesToUpload: UploadedFile[] = acceptedFiles.map((file) => {
        if (existingFileNames.has(file.name)) {
          return {
            id: `${file.name}-${file.size}-${Date.now()}`,
            file,
            progress: 0,
            status: 'failed',
            error: 'File already uploaded.',
          };
        } else {
          return {
            id: `${file.name}-${file.size}-${Date.now()}`,
            file,
            progress: 0,
            status: 'pending',
            error: null,
          };
        }
      });

      setUploadQueue((prev) => [...prev, ...newFilesToUpload]);

      newFilesToUpload.forEach(async (uploadedFile) => {
        if (uploadedFile.status === 'failed') {
          return; // Skip upload for duplicate files
        }

        setUploadQueue((prev) =>
          prev.map((f) => (f.id === uploadedFile.id ? { ...f, status: 'uploading' } : f)),
        );
        const uploadedFileDetails = await uploadFile(
          uploadedFile.file,
          deploymentId,
          (progress) => {
            setUploadQueue((prev) =>
              prev.map((f) => (f.id === uploadedFile.id ? { ...f, progress: progress } : f)),
            );
          },
        );

        if (uploadedFileDetails) {
          setUploadQueue((prev) =>
            prev.map((f) =>
              f.id === uploadedFile.id ? { ...f, status: 'completed', progress: 100 } : f,
            ),
          );
          // Add uploaded file with proper status - use backend status if available
          const backendStatus = uploadedFileDetails.status?.toUpperCase();
          let initialStatus: FileStatus;
          if (backendStatus === 'RUNNING' || backendStatus === 'PROCESSING') {
            initialStatus = FileStatus.RUNNING;
          } else if (backendStatus === 'COMPLETED') {
            initialStatus = FileStatus.COMPLETED;
          } else if (backendStatus === 'FAILED') {
            initialStatus = FileStatus.FAILED;
          } else {
            // Default to PENDING for newly uploaded files (Cloud Task will update to RUNNING)
            initialStatus = FileStatus.PENDING;
          }

          const uploadedFileWithStatus: FileAdminDetailWithUserExtended = {
            ...uploadedFileDetails,
            status: initialStatus,
          };
          setFiles((prevFiles) => {
            // Check if file already exists (avoid duplicates)
            const existingIndex = prevFiles.findIndex(
              (f) => f.file_id === uploadedFileDetails.file_id,
            );
            if (existingIndex >= 0) {
              // Update existing file
              const updated = [...prevFiles];
              updated[existingIndex] = uploadedFileWithStatus;
              return updated;
            }
            // Add new file at the beginning
            return [uploadedFileWithStatus, ...prevFiles];
          });
          // Immediately fetch files to get the latest status from server
          // This ensures the file appears with correct status and polling starts if needed
          // Use a small delay to allow server to process the upload
          setTimeout(() => {
            fetchFiles(false);
          }, 500);
        } else {
          setUploadQueue((prev) =>
            prev.map((f) =>
              f.id === uploadedFile.id ? { ...f, status: 'failed', error: 'Upload failed' } : f,
            ),
          );
        }
      });
    },
    [deploymentId, uploadFile, files],
  );

  const fetchFiles = useCallback(
    async (showLoading = false) => {
      if (!deploymentId) return;

      if (showLoading) {
        setFilesLoading(true);
      }
      setFilesError(null);

      try {
        const response =
          await clientService.api.getFilesForDeploymentApiV1AdminFilesDeploymentIdGet(deploymentId);

        setFiles((prevFiles) => {
          const serverFileIds = new Set(response.data.map((f) => f.file_id));

          const updatedFiles = response.data.map((file) => {
            let status: FileStatus;

            if (file.status) {
              const backendStatus = file.status.toUpperCase().trim();

              if (backendStatus === 'RUNNING' || backendStatus === 'PROCESSING') {
                status = FileStatus.RUNNING;
              } else if (backendStatus === 'COMPLETED') {
                status = FileStatus.COMPLETED;
                setInferencingFileIds((prev) => {
                  const next = new Set(prev);
                  next.delete(file.file_id);
                  return next;
                });
              } else if (backendStatus === 'FAILED') {
                status = FileStatus.FAILED;
                setInferencingFileIds((prev) => {
                  const next = new Set(prev);
                  next.delete(file.file_id);
                  return next;
                });
              } else {
                status = FileStatus.PENDING;
              }
            } else if (inferencingFileIdsRef.current.has(file.file_id)) {
              status = FileStatus.RUNNING;
            } else if (file.inferences && file.inferences.length > 0) {
              status = FileStatus.COMPLETED;
              setInferencingFileIds((prev) => {
                const next = new Set(prev);
                next.delete(file.file_id);
                return next;
              });
            }
            // Default to pending
            else {
              status = FileStatus.PENDING;
            }

            return {
              ...file,
              status,
            } as FileAdminDetailWithUserExtended;
          });

          // Preserve files that exist locally but aren't on server yet (recently uploaded)
          const localOnlyFiles = prevFiles.filter((f) => !serverFileIds.has(f.file_id));

          return [...updatedFiles, ...localOnlyFiles];
        });
      } catch (err) {
        setFilesError('Failed to fetch files.');
        console.error(err);
      } finally {
        if (showLoading) {
          setFilesLoading(false);
        }
      }
    },
    [deploymentId],
  );

  const hasPendingOrRunning = useMemo(
    () =>
      files.some(
        (f) =>
          f.status === FileStatus.PENDING ||
          f.status === FileStatus.RUNNING ||
          inferencingFileIds.has(f.file_id),
      ),
    [files, inferencingFileIds],
  );

  useEffect(() => {
    if (!hasPendingOrRunning) return;

    const interval = setInterval(() => {
      fetchFiles(false);
    }, 2000);

    return () => clearInterval(interval);
  }, [hasPendingOrRunning, fetchFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    disabled: !isAdmin,
  });

  useEffect(() => {
    const fetchDeploymentDetails = async () => {
      if (!deploymentId) return;
      setLoading(true);
      try {
        const response =
          await clientService.api.getDeploymentDetailsAdminApiV1AdminDeploymentsDeploymentIdGet(
            deploymentId,
          );
        setMasterData(response.data);
      } catch (err) {
        setError('Failed to fetch deployment details.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDeploymentDetails();
    fetchFiles(true);
  }, [deploymentId, fetchFiles]);

  const handleViewInference = (file: FileAdminDetailWithUserExtended) => {
    const data: Record<string, any>[] | null | undefined = file?.inferences?.map((f) => f?.data) as
      | Record<string, any>[]
      | null
      | undefined;
    setCurrentInferenceData(data as React.SetStateAction<Record<string, any>[] | null | undefined>);
    setOpenInferenceViewerDialog(true);
  };

  const handleViewAcousticIndices = (file: FileAdminDetailWithUserExtended) => {
    const data: Record<string, any>[] | null | undefined = file?.inferences?.map(
      (f) => f?.acoustic_indices,
    ) as Record<string, any>[] | null | undefined;

    setCurrentInferenceData(data as React.SetStateAction<Record<string, any>[] | null | undefined>);
    setOpenInferenceViewerDialog(true);
  };

  const handleDeleteFile = async () => {
    if (selectedFile) {
      try {
        await clientService.api.deleteApiFileApiV1AdminFilesFileIdDelete(selectedFile.file_id);
        setFiles((prevFiles) => prevFiles.filter((file) => file.file_id !== selectedFile.file_id));
      } catch (error) {
        showSnackbar({
          message: 'Failed to delete file',
          type: snackbarType.ERROR,
        });
      }
    }
    setOpenConfirmDialog(false);
  };

  const handleTriggerInference = async (file: FileAdminDetailWithUserExtended) => {
    // Immediately set status to RUNNING to avoid showing FAILED status
    setInferencingFileIds((prev) => {
      const next = new Set(prev);
      next.add(file.file_id);
      return next;
    });

    setFiles((prevFiles) =>
      prevFiles.map((f) => {
        if (f.file_id === file.file_id) {
          return { ...f, status: FileStatus.RUNNING };
        }
        return f;
      }),
    );

    try {
      // Trigger inference - this might return immediately but inference could still be processing
      await clientService.api.runInferenceOnFileApiV1AdminInferenceFileFileIdPost(file.file_id);

      // Keep status as RUNNING - polling will detect when inference is actually complete
      // Don't remove from inferencingFileIds yet - polling will handle that when complete
      // Immediately fetch files to check if inference completed synchronously
      await fetchFiles(false);
    } catch (error) {
      showSnackbar({
        message: 'Failed to trigger inference',
        type: snackbarType.ERROR,
      });
      setFiles((prevFiles) =>
        prevFiles.map((f) => {
          if (f.file_id === file.file_id) {
            return { ...f, status: FileStatus.FAILED };
          }
          return f;
        }),
      );
      // Remove from inferencingFileIds on error
      setInferencingFileIds((prev) => {
        const next = new Set(prev);
        next.delete(file.file_id);
        return next;
      });
    }
    // Note: Don't remove from inferencingFileIds in finally - let polling handle it when complete
  };

  const handleMarkComplete = async () => {
    if (!deploymentId) return;

    setMarkingComplete(true);

    try {
      await clientService.api.updateApiDeploymentApiV1AdminDeploymentsDeploymentIdPut(
        deploymentId,
        {
          data_upload_status: true,
        },
      );

      setMasterData((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          deployment: {
            ...prev.deployment,
            data_upload_status: true,
          },
        };
      });
    } catch (error) {
      showSnackbar({
        message: 'Failed to mark as complete',
        type: snackbarType.ERROR,
      });
    } finally {
      setMarkingComplete(false);
      setOpenMarkCompleteDialog(false);
    }
  };

  const handleRequestSort = (
    property: keyof FileAdminDetailWithUserExtended | 'uploaded_by_user',
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property as keyof FileAdminDetailWithUserExtended | 'uploaded_by_user');
  };

  const sortedFiles = useMemo(() => {
    return [...files].sort((a, b) => {
      const aValue =
        orderBy === 'uploaded_by_user'
          ? a.uploaded_by_user.first_name
          : a[orderBy as keyof FileAdminDetailWithUserExtended];
      const bValue =
        orderBy === 'uploaded_by_user'
          ? b.uploaded_by_user.first_name
          : b[orderBy as keyof FileAdminDetailWithUserExtended];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [files, order, orderBy]);

  const paginatedFiles = sortedFiles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!masterData) {
    return <Typography variant="h6">Deployment not found.</Typography>;
  }

  const { project, site, location, deployment, device } = masterData;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ px: 4, py: 4, backgroundColor: '#0A0A0A', minHeight: '100vh', color: '#FFFFFF' }}>
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          p: 3,
          backgroundColor: 'rgba(17, 17, 17, 0.6)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '24px',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: '#FFF' }}>
            Project Details
          </Typography>
          {!deployment.data_upload_status && (
            <Button
              variant="contained"
              onClick={() => setOpenMarkCompleteDialog(true)}
              disabled={files.length === 0 || markingComplete}
              sx={{
                bgcolor: '#10B981',
                color: '#FFFFFF',
                borderRadius: '12px',
                px: 3,
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': { bgcolor: '#059669' },
                '&.Mui-disabled': { 
                  bgcolor: 'rgba(16, 185, 129, 0.15)', 
                  color: 'rgba(255, 255, 255, 0.3)',
                  border: '1px solid rgba(16, 185, 129, 0.2)'
                }
              }}
            >
              {markingComplete ? 'Marking...' : 'Mark Complete'}
            </Button>
          )}
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BusinessCenterIcon sx={{ mr: 1.5, color: '#10B981', fontSize: 20 }} />
                  <Typography variant="body1" sx={{ color: '#FFF', fontWeight: 600 }}>
                    <Box component="span" sx={{ color: '#666', fontWeight: 600, mr: 1.5, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Project</Box>
                    <Link to={`/admin/project/${project.project_id}`} style={{ color: '#3B82F6', textDecoration: 'none' }}>
                      {project.project_name}
                    </Link>
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LandscapeIcon sx={{ mr: 1.5, color: '#3B82F6', fontSize: 20 }} />
                  <Typography variant="body1" sx={{ color: '#FFF', fontWeight: 600 }}>
                    <Box component="span" sx={{ color: '#666', fontWeight: 600, mr: 1.5, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Site</Box>
                    <Link
                      to={`/admin/project/${project.project_id}/site/${site.site_id}`}
                      style={{ color: '#3B82F6', textDecoration: 'none' }}
                    >
                      {site.site_name}
                    </Link>
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PlaceIcon sx={{ mr: 1.5, color: '#F59E0B', fontSize: 20 }} />
                  <Typography variant="body1" sx={{ color: '#FFF', fontWeight: 600 }}>
                    <Box component="span" sx={{ color: '#666', fontWeight: 600, mr: 1.5, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Location</Box>
                    {location.location_name}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SettingsIcon sx={{ mr: 1.5, color: '#8B5CF6', fontSize: 20 }} />
                  <Typography variant="body1" sx={{ color: '#FFF', fontWeight: 600 }}>
                    <Box component="span" sx={{ color: '#666', fontWeight: 600, mr: 1.5, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Deployment</Box>
                    {deployment.deployment_name}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CalendarMonth sx={{ mr: 1.5, color: '#EC4899', fontSize: 20 }} />
                  <Typography variant="body1" sx={{ color: '#FFF', fontWeight: 600 }}>
                    <Box component="span" sx={{ color: '#666', fontWeight: 600, mr: 1.5, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Period</Box>
                    {formatDate(deployment.start_date)} - {formatDate(deployment.end_date)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          
          <Grid item xs={12} md={5}>
            <DeploymentDeviceCard deploymentId={deployment.deployment_id} projectId={project.project_id} />
          </Grid>
        </Grid>
      </Paper>

      {deployment.data_upload_status ? (
        <Alert
          severity="info"
          sx={{
            bgcolor: 'rgba(59, 130, 246, 0.1)',
            color: '#93C5FD',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '16px',
            '& .MuiAlert-icon': { color: '#3B82F6' }
          }}
        >
          This deployment has been marked as complete. No further uploads are possible. If you need
          to upload additional data, please contact administrative support for assistance.
        </Alert>
      ) : isAdmin ? (
        <Paper
          elevation={0}
          sx={{
            mb: 4,
            p: 5,
            minHeight: 220,
            border: '2px dashed rgba(255, 255, 255, 0.1)',
            backgroundColor: 'rgba(255, 255, 255, 0.02)',
            backdropFilter: 'blur(8px)',
            borderRadius: '24px',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
            overflow: 'hidden',
            position: 'relative',
            '&:hover': {
              borderColor: '#10B981',
              backgroundColor: 'rgba(16, 185, 129, 0.05)',
              transform: 'scale(1.005)',
              boxShadow: '0 0 40px rgba(16, 185, 129, 0.1) inset'
            },
          }}
          {...getRootProps()}
        >
          <input {...getInputProps()} />

          <Typography variant="body1" sx={{ mb: 2, fontSize: '1.2rem', fontWeight: 600, color: '#FFF' }}>
            {isDragActive
              ? 'Drop the files here...'
              : 'Drag & drop files here, or click to select files.'}
          </Typography>

          <Alert
            severity="info"
            sx={{
              mb: 3,
              width: '100%',
              maxWidth: 500,
              bgcolor: 'rgba(59, 130, 246, 0.1)',
              color: '#93C5FD',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              borderRadius: '12px',
              '& .MuiAlert-icon': { color: '#3B82F6' }
            }}
          >
            Please upload files relevant to a <strong>{device?.type || 'assigned'}</strong> device. Accepted
            formats: {acceptedFormats}.
          </Alert>

          <Typography
            variant="body2"
            sx={{
              color: '#666',
              mt: 1,
              maxWidth: 420,
              lineHeight: 1.6,
              fontWeight: 500
            }}
          >
            <Box component="span" sx={{ color: '#F59E0B' }}>N/A</Box> Upload no more than <strong>10 files</strong> at a time. Each
            file should be <strong>5 MB</strong> or smaller.
          </Typography>
        </Paper>
      ) : (
        <Typography
          variant="body2"
          sx={{ mb: 4, fontSize: '12px', color: '#666', fontWeight: 500, lineHeight: 1.6 }}
        >
          Uploads are handled by administrators.
        </Typography>
      )}

      <UploadStatusDialog
        open={isUploadDialogVisible}
        onClose={() => setIsUploadDialogVisible(false)}
        uploadQueue={uploadQueue}
        onClearFile={handleClearFile}
        onClearAll={handleClearAllUploads}
      />

      <Typography variant="h4" sx={{ mb: 4, fontWeight: 800, color: '#FFF' }}>
        Uploaded Files
      </Typography>
      {filesLoading ? (
        <CircularProgress />
      ) : filesError ? (
        <Alert severity="error">{filesError}</Alert>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            bgcolor: 'rgba(17, 17, 17, 0.6)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '24px',
            overflow: 'hidden'
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(255, 255, 255, 0.03)' }}>
                {columns.map((column) => {
                  if (deployment.data_upload_status && column.id === 'actions') return null;
                  return (
                    <TableCell
                      key={column.id}
                      align="justify"
                      sx={{
                        color: '#666',
                        fontWeight: 700,
                        fontSize: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                        py: 2.5
                      }}
                    >
                      {column.sortable ? (
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : 'asc'}
                          onClick={() =>
                            handleRequestSort(
                              column.id as
                                | keyof FileAdminDetailWithUserExtended
                                | 'uploaded_by_user',
                            )
                          }
                          sx={{
                            color: '#666 !important',
                            '&.Mui-active': { color: '#FFF !important' },
                            '& .MuiTableSortLabel-icon': { color: '#10B981 !important' }
                          }}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedFiles.map((file) => (
                <TableRow
                  key={file.file_id}
                  sx={{
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.02)' },
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  <TableCell sx={{ color: '#FFF', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', py: 2 }}>{file.name}</TableCell>
                  <TableCell sx={{ color: '#FFF', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>{file.uploaded_by_user.first_name}</TableCell>
                  <TableCell sx={{ color: '#666', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    {formatDistanceToNow(new Date(file.created_at + 'Z'), { addSuffix: true })}
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    {file.status === FileStatus.RUNNING || inferencingFileIds.has(file.file_id) ? (
                      <Chip
                        label="Processing"
                        size="small"
                        icon={<CircularProgress size={16} sx={{ color: '#F59E0B' }} />}
                        sx={{
                          bgcolor: 'rgba(245, 158, 11, 0.1)',
                          color: '#F59E0B',
                          fontWeight: 700,
                          borderRadius: '8px',
                          border: '1px solid rgba(245, 158, 11, 0.2)'
                        }}
                      />
                    ) : file.status === FileStatus.COMPLETED ? (
                      <Chip
                        label="Completed"
                        size="small"
                        icon={<CheckCircle sx={{ color: '#10B981', fontSize: '16px !important' }} />}
                        sx={{
                          bgcolor: 'rgba(16, 185, 129, 0.1)',
                          color: '#10B981',
                          fontWeight: 700,
                          borderRadius: '8px',
                          border: '1px solid rgba(16, 185, 129, 0.2)'
                        }}
                      />
                    ) : file.status === FileStatus.FAILED ? (
                      <Chip
                        label="Failed"
                        size="small"
                        icon={<Error sx={{ color: '#EF4444', fontSize: '16px !important' }} />}
                        sx={{
                          bgcolor: 'rgba(239, 68, 68, 0.1)',
                          color: '#EF4444',
                          fontWeight: 700,
                          borderRadius: '8px',
                          border: '1px solid rgba(239, 68, 68, 0.2)'
                        }}
                      />
                    ) : (
                      <Chip
                        label="Pending"
                        size="small"
                        sx={{
                          bgcolor: 'rgba(107, 114, 128, 0.1)',
                          color: '#9CA3AF',
                          fontWeight: 700,
                          borderRadius: '8px',
                          border: '1px solid rgba(107, 114, 128, 0.2)'
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    {masterData.device?.type === DeviceType.AUDIO ? (
                      <AudioPlayer fileId={file.file_id} />
                    ) : masterData.device?.type === DeviceType.CAMERA ? (
                      <ImageOverlay
                        alt={file.name}
                        getFileUrl={() =>
                          clientService.api
                            .getDownloadUrlApiV1AdminFilesFileIdDownloadUrlGet(file.file_id)
                            .then(
                              (res: HttpResponse<FileDownloadResponse, HTTPValidationError>) =>
                                res.data.download_url,
                            )
                        }
                      />
                    ) : (
                      <IconButton
                        component="a"
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        sx={{ color: '#10B981' }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    {file.inferences && file.inferences.length > 0 && (
                      <IconButton onClick={() => handleViewInference(file)} sx={{ color: '#3B82F6' }}>
                        <DataObject />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    {file.inferences && file.inferences.length > 0 && (
                      <ImageOverlay
                        alt={file.name}
                        getFileUrl={() =>
                          clientService.api
                            .getSpectrogramUrlApiV1AdminFilesFileIdSpectrogramUrlGet(file.file_id)
                            .then(
                              (res: HttpResponse<FileDownloadResponse, HTTPValidationError>) =>
                                res.data.download_url,
                            )
                        }
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    {file.inferences && file.inferences.length > 0 && (
                      <IconButton onClick={() => handleViewAcousticIndices(file)} sx={{ color: '#F59E0B' }}>
                        <DataObject />
                      </IconButton>
                    )}
                  </TableCell>
                  {!deployment.data_upload_status && (
                    <TableCell align="left" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                      {isAdmin && (
                        <IconButton
                          onClick={() => {
                            setSelectedFile(file);
                            setOpenConfirmDialog(true);
                          }}
                          sx={{ color: '#EF4444' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                      {(!file.inferences || file.inferences.length === 0) &&
                        !inferencingFileIds.has(file.file_id) &&
                        file.status !== FileStatus.RUNNING &&
                        file.status !== FileStatus.PENDING &&
                        file.status !== FileStatus.COMPLETED && (
                          <IconButton onClick={() => handleTriggerInference(file)} sx={{ color: '#8B5CF6' }}>
                            <InsightsIcon />
                          </IconButton>
                        )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
            component="div"
            count={files.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              color: '#666',
              borderTop: '1px solid rgba(255, 255, 255, 0.05)',
              '& .MuiTablePagination-selectIcon': { color: '#666' },
              '& .MuiTablePagination-actions': { color: '#666' },
              '& .MuiIconButton-root.Mui-disabled': { color: 'rgba(255, 255, 255, 0.1)' }
            }}
          />
        </TableContainer>
      )}
      <ConfirmationDialog
        open={openConfirmDialog}
        onCancel={() => setOpenConfirmDialog(false)}
        onConfirm={handleDeleteFile}
        title="Delete File"
        content="Are you sure you want to delete this file?"
      />
      <ConfirmationDialog
        open={openMarkCompleteDialog}
        onCancel={() => setOpenMarkCompleteDialog(false)}
        onConfirm={handleMarkComplete}
        title="Mark As Complete"
        content="Are you sure you want to mark this deployment as complete? Once completed, you will not be able to upload any data for this deployment."
        confirmButtonText="Yes, Mark as Complete"
      />
      <InferenceViewerDialog
        open={openInferenceViewerDialog}
        onClose={() => setOpenInferenceViewerDialog(false)}
        inferenceData={currentInferenceData}
      />
    </Box>
  );
};

export default Upload;
